/* eslint-disable no-console */
/* eslint-disable no-unused-expressions */

/**************************************************************************
 * Five9BridgeClient Class
 *
 * This javascript Class is used in interface to the Five9 Bridge Server.
 * The Five9 Bridge Server provides a realtime mechanism for posting messages
 * to a client which are sent to the client via a WebSocket connection to
 * the Bridge server.
 *
 * This class mainly supports the client's connection to Bridge Server and
 * through callbacks, notifies the client of inbound messages and connection
 * status.  It also provides utility functions that can be used to REST post
 * messages to the client as well as delete the client connection via REST.
 **************************************************************************/
var self;
class Five9BridgeClient  {

    //**************************************************************************
    constructor() {
        this.VERSION = '1.0.0';

        this.openEventCallback;
        this.dataEventCallback;
        this.closeEventCallback;
        this.closeError;

        this.socketURL;
        this.domain;
        this.websocket;
        this.restURL;

        self = this;

        // Setup a timer to send a ping message over the WebSocket every 30 seconds
        // eslint-disable-next-line @lwc/lwc/no-async-operation
        setInterval(function() {
            // Send a ping request
            self._sendPing();
        }, 30000);
    }

    //**************************************************************************
    _sendPing() {
        if (!this.isOpen())
            return;

        let message = {
            type: "ping"
        };

        this._log("Sending PING on socket");
        try {
            let jsonMessage = JSON.stringify(message);
            this.websocket.send(jsonMessage);
        } catch (ex) {
            this._log("Failed sending ping");
            this._CloseWebSocketOnError("Failed trying to ping the Bridge Server");
        }
    }

    //**************************************************************************
    _log(message, error) {
        if (error) {
            console.error(`Five9 Bridge Client ERROR: ${message}`);
        } else {
            console.info(`Five9 Bridge Client: ${message}`);
        }
    }

    //**************************************************************************
    _connectWebSocket(wsURL) {
        this._log("WebSocket connect to: " + wsURL);
        try {
            this.websocket = new WebSocket(wsURL);
            this.websocket.onopen = evt => this._onOpen(evt);
            this.websocket.onclose = evt => this._onClose(evt);
            this.websocket.onmessage = evt => this._onMessage(evt);
            this.websocket.onerror = evt => this._onError(evt);
        } catch (ex) {
            this._log(`Five9 Bridge Client: Exception opening web socket: ${ex.message}`, true);
        }
    }

    //**************************************************************************
    _onOpen() {
        this._log("WebSocket opened, send our Domain to the Bridge Server");

        // Send this client's domain
        this._sendDomain();
    }

    //**************************************************************************
    _onClose() {
        this._log('Cleanup on connection closed');

        if (this.closeEventCallback) {
            this.closeEventCallback(this.closeError);
        }

        this.websocket = undefined;
        this.dataEventCallback = undefined;
        this.openEventCallback = undefined;
        this.closeEventCallback = undefined;
        this.closeError = undefined;
        this.restURL = undefined;
        this.socketURL = undefined;
        this.domain = undefined;
    }

    //**************************************************************************
    _onMessage(evt) {
        // this._log(evt.data);
        let message = JSON.parse(evt.data);
        let messageType = message.type;
        if (messageType === "data") {
            let messageData = JSON.stringify(message.data);
            this._log(`Data: ${messageData}`);

            // Notify the app
            this.dataEventCallback(messageData);
            return;
        }

        if (messageType === 'url') {
            this.restURL = message.data;
            this._log(`URL: ${this.restURL}`);

            // Did the app want to be notified?
            if (this.openEventCallback)
                this.openEventCallback(this.restURL);
            return;
        }

        if (messageType === 'error') {
            let errorMessage = message.message;
            this._log(`Error: ${errorMessage}`);

            // Notify the app
            this._CloseWebSocketOnError(errorMessage);
            return;
        }

        this._log(`Received unexpected Web Socket Message: ${messageType}`);
    }

    //**************************************************************************
    _onError() {
        // While onError delivers an event is doesn't give us the reason which is odd
        // because the socket code logs the reason for the failure.
        this._log(`ERROR: onError websocket callback`);
        self.closeError = new Error('Error trying to connect to the WebSocket URL');

        // Note, we don't close the socket as that is happening automatically when we get an error
    }

    //**************************************************************************
    _CloseWebSocketOnError(message) {
        self._log(`${message}: Closing WebSocket`, true);
        self.closeError = new Error(message);
        self.websocket.close();
    }

    //**************************************************************************
    _getDomainAndOpenSocket() {
        this._log(`Get the Five9 user domain and send to the bride server over the WebSocket`);
        this._getMetaData();
    }

    //**************************************************************************
    _getMetaData() {
        this._log(`Get Five9 MetaData (this validates the user is logged in)`);
        fetch('https://app.five9.com/appsvcs/rs/svc/auth/metadata', {
            cache: 'no-cache',
            credentials: 'include', // include, same-origin, *omit
            mode: 'cors' // no-cors, cors, *same-origin.
        })
            .then(function(response) {
                // Good Response
                if (response.status === 200) {
                    self._log('OK (200) response getting Five9 Metadata');
                    return response.json();
                }

                // Authentication Failure
                if (response.status === 401) {
                    self.closeError = new Error(`User is not logged in`);
                    self._onClose();
                    return;
                }

                self.closeError = new Error(`Failed trying to get Five9 Metadata, response status: ${response.status}`);
                self._onClose();
            })
            .then(f9md => self._getOrgInfo(f9md))
            .catch(() => function(err) {
                self.closeError = new Error(`Exception trying to get Five9 Metadata: ${err.message}`);
                self._onClose();
            });
    }

    //*******************************************************
    _getOrgInfo(f9md) {
        let orgId = f9md.orgId;

        let host = f9md.metadata.dataCenters[0].apiUrls[0].host;
        let port = f9md.metadata.dataCenters[0].apiUrls[0].port;
        let baseURL = `https://${host}:${port}`;
        let url = `${baseURL}/appsvcs/rs/svc/orgs/${orgId}`;

        self._log(`Get Five9 Org information for org Id: ${orgId}`);
        fetch(url, {
            cache: 'no-cache',
            credentials: 'include', // include, same-origin, *omit
            mode: 'cors' // no-cors, cors, *same-origin.
        })
            .then(function (response) {
                if (response.status === 200) {
                    self._log('OK (200) response getting Five9 Org Info');
                    return response.json();
                }
                self.closeError = new Error(`Failed trying to get Five9 Org info, response status: ${response.status}`);
                self._onClose();
            })
            .then( orgInfo => {
                self.domain = orgInfo.name;
                self._log(`"Got domain ${self.domain}, open the WebSocket to the Bridge Server`);
                self._connectWebSocket(self.socketURL);
            })
            .catch(() => function (err) {
                self.closeError = new Error(`Exception trying to get Five9 Org Info: ${err.message}`);
                self._onClose();
            })
    }

    //*******************************************************
    _sendDomain() {
        // OK, here is where we send our domain to the server over the web socket
        let message = {
            type: "domain",
            data: self.domain
        };

        try {
            let jsonMessage = JSON.stringify(message);
            self.websocket.send(jsonMessage);
        } catch (ex) {
            self._CloseWebSocketOnError(`Failed trying send domain to Bridge Server`);
        }
    }


    //**************************************************************************
    // External Interface
    //**************************************************************************

    //**************************************************************************
    open(dataEvent, openEvent, closeEvent, wsURL) {
        if (this.isOpen()) {
            throw(new Error('Open WebSocket called but websocket already open'));
        }

        // Make sure they passed the dataEvent callback.
        // The urlEvent callback is optional
        if (!dataEvent) {
            throw(new Error('Open WebSocket called without dataEvent parameter'));
        }

        // If they don't pass a URL then use the default
        if (!wsURL) {
            wsURL = "wss://psapps002.atl.five9.com/ps-bridge/bridge";
        }

        // Save the socket URL we should use
        this.socketURL = wsURL;

        // Save the callback functions
        this.dataEventCallback = dataEvent;
        this.openEventCallback = openEvent;
        this.closeEventCallback = closeEvent;
        this.closeError = undefined;

        // Get this client's domain and then open the WebSocket to the Bridge server and send it.
        this._getDomainAndOpenSocket();
        //this._connectWebSocket(wsURL);
    }

    //**************************************************************************
    close() {
        if (!this.isOpen()) {
            throw(new Error('Close WebSocket called but websocket not open'));
        }
        this._log('Close WebSocket');
        this.closeError = undefined;
        this.websocket.close();
    }

    //**************************************************************************
    isOpen() {
        return (!!this.websocket);
    }

    //**************************************************************************
    postToBridge(jsonData, callbackResult) {
        let xhr = new XMLHttpRequest();

        xhr.open('POST', this.restURL);
        xhr.setRequestHeader("Content-Type", "application/json;charset=UTF-8");
        xhr.onreadystatechange = function() {
            if (xhr.readyState === XMLHttpRequest.DONE) {
                if (callbackResult) {
                    callbackResult(xhr.status, xhr.responseText);
                }
            }
        };
        xhr.send(jsonData);
    }

    //**************************************************************************
    deleteToBridge(callbackResult) {
        let xhr = new XMLHttpRequest();

        xhr.open('DELETE', this.restURL);
        xhr.setRequestHeader("Content-Type", "application/json;charset=UTF-8");
        xhr.onreadystatechange = function() {
            if (xhr.readyState === XMLHttpRequest.DONE) {
                if (callbackResult) {
                    callbackResult(xhr.status, xhr.responseText);
                }
            }
        };
        xhr.send();
    }

    //**************************************************************************
    getVersion() {
        return this.VERSION;
    }
}

export { Five9BridgeClient };

/**************************************************************************
 *
 * ----------------------- Five9BridgeClient END --------------------------
 *
 **************************************************************************/
